import {
  Box,
  Button,
  Center,
  FormControl,
  FormLabel,
  Heading,
  Select,
  SimpleGrid,
  Spinner,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { FiFileText } from "react-icons/fi";
import { ButtonBack, Layout, PdfVisualizacaoModal } from "../../../components";
import { useIsAuth } from "../../../hooks/useIsAuth";
import { RootState } from "../../app/mainReducer";
import { saerpTransApiCall } from "../../../utils/apiCall";
import { formatAxiosErrorMessage } from "../../../utils/formatError";
import { getAnoPadraoInforme, getAnosDisponiveisInformeRendimento } from "../utils/informeRendimentoAnos";

const MESES_NASCIMENTO = [
  { valor: 1, nome: "Janeiro" },
  { valor: 2, nome: "Fevereiro" },
  { valor: 3, nome: "Março" },
  { valor: 4, nome: "Abril" },
  { valor: 5, nome: "Maio" },
  { valor: 6, nome: "Junho" },
  { valor: 7, nome: "Julho" },
  { valor: 8, nome: "Agosto" },
  { valor: 9, nome: "Setembro" },
  { valor: 10, nome: "Outubro" },
  { valor: 11, nome: "Novembro" },
  { valor: 12, nome: "Dezembro" },
];

const InformeRendimentoMotorista = () => {
  useIsAuth();
  const networkOnline = useSelector((state: RootState) => state.login.networkOnline || false);
  const toast = useToast();

  const anosDisponiveis = useMemo(() => getAnosDisponiveisInformeRendimento(), []);
  const anoPadrao = useMemo(() => getAnoPadraoInforme(anosDisponiveis), [anosDisponiveis]);
  const umAnoApenas = anosDisponiveis.length == 1;

  const anosNascimentoOpcoes = useMemo(() => {
    const hoje = new Date();
    const anoMax = hoje.getFullYear();
    const anoMin = anoMax - 100;
    const out: number[] = [];
    for (let y = anoMax; y >= anoMin; y--) {
      out.push(y);
    }
    return out;
  }, []);

  const [anoSelecionado, setAnoSelecionado] = useState<number | null>(anoPadrao);
  const [anoNasc, setAnoNasc] = useState("");
  const [mesNasc, setMesNasc] = useState("");
  const [diaNasc, setDiaNasc] = useState("");
  const [carregando, setCarregando] = useState(false);
  const [modalAberto, setModalAberto] = useState(false);
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);

  const fecharPdf = () => {
    setPdfBlob(null);
    setModalAberto(false);
  };

  const maxDiaNoMesNascimento = useMemo(() => {
    const y = parseInt(anoNasc, 10);
    const m = parseInt(mesNasc, 10);
    if (!y || !m) {
      return 31;
    }
    return new Date(y, m, 0).getDate();
  }, [anoNasc, mesNasc]);

  useEffect(() => {
    const d = parseInt(diaNasc, 10);
    if (diaNasc && (d < 1 || d > maxDiaNoMesNascimento)) {
      setDiaNasc("");
    }
  }, [anoNasc, mesNasc, maxDiaNoMesNascimento, diaNasc]);

  const dataNascimento = useMemo(() => {
    if (!anoNasc || !mesNasc || !diaNasc) {
      return "";
    }
    const y = parseInt(anoNasc, 10);
    const m = parseInt(mesNasc, 10);
    const d = parseInt(diaNasc, 10);
    if (y < 1 || m < 1 || m > 12 || d < 1 || d > maxDiaNoMesNascimento) {
      return "";
    }
    return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  }, [anoNasc, mesNasc, diaNasc, maxDiaNoMesNascimento]);

  const selectEstiloNascimento = {
    bg: "white",
    borderColor: "gray.300",
    size: "lg" as const,
    _dark: { bg: "gray.700", borderColor: "gray.500" },
  };

  const buscarInforme = useCallback(
    async (ano: number) => {
      if (!networkOnline) {
        toast({
          title: "Sem internet",
          description: "Conecte-se à internet para gerar o informe. Você pode voltar para a tela de cargas pelo botão acima.",
          status: "warning",
          duration: 8000,
          isClosable: true,
        });
        return;
      }
      if (!dataNascimento) {
        toast({
          title: "Data de nascimento",
          description: "Preencha sua data de nascimento para continuar. Isso confirma que o IR é seu.",
          status: "warning",
          duration: 7000,
          isClosable: true,
        });
        return;
      }
      setCarregando(true);
      try {
        const { data } = await saerpTransApiCall({
          method: "post",
          url: "/gestao-de-entrega/v2/informe-rendimento-rpa",
          data: { ano, data_nascimento: dataNascimento },
          responseType: "blob",
        });
        setPdfBlob(data as Blob);
        setModalAberto(true);
      } catch (error) {
        const description = await formatAxiosErrorMessage(error);
        toast({
          title: "Não foi possível gerar o informe",
          description,
          status: "error",
          duration: 9000,
          isClosable: true,
        });
      } finally {
        setCarregando(false);
      }
    },
    [networkOnline, toast, dataNascimento]
  );

  const tituloPdf =
    anoSelecionado != null ? `Informe de rendimentos (IR) — ano ${anoSelecionado}` : "Informe de rendimentos (IR)";
  const nomeArquivoPdf =
    anoSelecionado != null ? `Informe_rendimentos_IR_${anoSelecionado}.pdf` : "Informe_rendimentos_IR.pdf";

  return (
    <Layout canLogout={true} showFooter={true} backTo="/documentos" title={<ButtonBack to="/documentos" />}>
      <VStack spacing={4} align="stretch" px={2} pb={6} pt={2} maxW="600px" mx="auto">
        <Center>
          <Heading size="md" textAlign="center" color="gray.800" _dark={{ color: "gray.100" }}>
            Informe de rendimentos
          </Heading>
        </Center>

        {anosDisponiveis.length == 0 && (
          <Box
            bg="yellow.50"
            borderRadius="md"
            p={4}
            borderWidth="1px"
            borderColor="yellow.200"
            _dark={{ bg: "yellow.900", borderColor: "yellow.700" }}
          >
            <Text color="gray.800" _dark={{ color: "gray.100" }}>
              Ainda não há informe disponível para download. Quando o período for liberado, esta opção aparecerá na lista
              de cargas.
            </Text>
          </Box>
        )}

        {anosDisponiveis.length > 0 && (
          <VStack spacing={4} align="stretch">
            <Box
              bg="white"
              borderRadius="md"
              p={4}
              borderWidth="1px"
              borderColor="gray.200"
              shadow="sm"
              _dark={{ bg: "gray.700", borderColor: "gray.600" }}
            >
              <Text fontSize="sm" color="gray.700" _dark={{ color: "gray.200" }} lineHeight="tall">
                Por segurança, precisamos da sua{" "}
                <Text as="span" fontWeight="semibold">
                  data de nascimento
                </Text>.
              </Text>
            </Box>
            <FormControl>
              <FormLabel color="gray.800" _dark={{ color: "gray.100" }} fontSize="md">
                Data de nascimento
              </FormLabel>
              <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={3}>
                <FormControl>
                  <FormLabel fontSize="sm" color="gray.700" _dark={{ color: "gray.200" }}>
                    Ano
                  </FormLabel>
                  <Select
                    placeholder="Ano"
                    value={anoNasc}
                    onChange={(e) => setAnoNasc(e.target.value)}
                    {...selectEstiloNascimento}
                  >
                    {anosNascimentoOpcoes.map((ano) => (
                      <option key={ano} value={String(ano)}>
                        {ano}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" color="gray.700" _dark={{ color: "gray.200" }}>
                    Mês
                  </FormLabel>
                  <Select
                    placeholder="Mês"
                    value={mesNasc}
                    onChange={(e) => setMesNasc(e.target.value)}
                    {...selectEstiloNascimento}
                  >
                    {MESES_NASCIMENTO.map((mes) => (
                      <option key={mes.valor} value={String(mes.valor)}>
                        ({String(mes.valor).padStart(2, "0")}) {mes.nome}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" color="gray.700" _dark={{ color: "gray.200" }}>
                    Dia
                  </FormLabel>
                  <Select
                    placeholder="Dia"
                    value={diaNasc}
                    onChange={(e) => setDiaNasc(e.target.value)}
                    {...selectEstiloNascimento}
                  >
                    {Array.from({ length: maxDiaNoMesNascimento }, (_, i) => i + 1).map((dia) => (
                      <option key={dia} value={String(dia)}>
                        {dia}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </SimpleGrid>
            </FormControl>

            {umAnoApenas && (
              <Box
                bg="blue.50"
                borderRadius="md"
                p={4}
                borderWidth="1px"
                borderColor="blue.200"
                _dark={{ bg: "blue.900", borderColor: "blue.700" }}
              >
                <Text fontWeight="600" color="gray.800" _dark={{ color: "gray.100" }} mb={1}>
                  Ano disponível: {anosDisponiveis[0]}
                </Text>
                {!carregando && (
                  <Button mt={3} colorScheme="blue" width="full" onClick={() => buscarInforme(anosDisponiveis[0])}>
                    Abrir informe em PDF
                  </Button>
                )}
                {carregando && (
                  <Center mt={4}>
                    <Spinner size="lg" color="blue.500" />
                  </Center>
                )}
              </Box>
            )}

            {anosDisponiveis.length > 1 && (
              <VStack spacing={4} align="stretch">
                <FormControl>
                  <FormLabel color="gray.800" _dark={{ color: "gray.100" }} fontSize="md">
                    Escolha o ano do seu informe de rendimentos (IR)
                  </FormLabel>
                  <Select
                    bg="white"
                    borderColor="gray.300"
                    size="lg"
                    _dark={{ bg: "gray.700", borderColor: "gray.500" }}
                    value={anoSelecionado ?? ""}
                    onChange={(e) => setAnoSelecionado(parseInt(e.target.value, 10))}
                  >
                    {[...anosDisponiveis].reverse().map((ano) => (
                      <option key={ano} value={ano}>
                        {ano}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  colorScheme="blue"
                  size="lg"
                  width="full"
                  onClick={() => anoSelecionado != null && buscarInforme(anoSelecionado)}
                  isLoading={carregando}
                  isDisabled={anoSelecionado == null}
                >
                  <FiFileText fontSize="1.3em" /> <Text ml={1}>Abrir informe de rendimentos (IR)</Text>
                </Button>
              </VStack>
            )}
          </VStack>
        )}
      </VStack>

      <PdfVisualizacaoModal
        isOpen={modalAberto}
        onClose={fecharPdf}
        pdfBlob={pdfBlob}
        nomeArquivoSugerido={nomeArquivoPdf}
        titulo={tituloPdf}
        tituloIframe={tituloPdf}
      />
    </Layout>
  );
};

export default InformeRendimentoMotorista;
